import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import { Formik, FormikHelpers } from "formik";
import { ScreenFlowFormButtons } from "./ScreenFlowFormButtons";
import { objectVals2String } from "../../../utils/objectVals2String";
import { IconButton } from "./IconButton";

interface Values {
  checked: string;
  response?: string;
}

export type SelectButton = {
  key: string;
  text: string;
  icon?: JSX.Element;
  response?: string;
};

interface Props {
  initChecked?: string;
  answerLabel?: string;
  selectButtons: SelectButton[];
  required?: boolean;
  errorText?: string;
  onComplete: (checked: string) => Promise<boolean>;
  onBack?: () => void;
}

export const SingleSelectButtonForm = ({
  initChecked,
  selectButtons,
  required,
  errorText,
  onComplete,
  onBack,
}: Props): JSX.Element => {
  // https://formik.org/docs/api/formik

  const initValues: Values = {
    checked: initChecked || "",
    response:
      selectButtons.find((button) => button.key == initChecked)?.response || "",
  };

  const formikInit = {
    values: initValues,
  };

  const handleSubmit = async (
    values: Values,
    { setSubmitting, setStatus }: FormikHelpers<Values>
  ) => {
    setStatus("");
    try {
      if (required && values.checked === "") {
        setStatus(errorText || "Please select an option to continue.");
        setSubmitting(false);
      } else {
        await onComplete(values.checked);
      }
    } catch (error) {
      setStatus("Sorry, error: " + ((error as Error)?.message || ""));
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={formikInit.values}
      onSubmit={handleSubmit}
      key={objectVals2String(formikInit.values)}
    >
      {({
        setFieldValue,
        handleSubmit,
        isSubmitting,
        values,
        status,
        setStatus,
      }) => (
        <form onSubmit={handleSubmit}>
          <Grid
            container
            spacing={1}
            sx={{
              my: 1,
            }}
          >
            <Grid item xs={12}>
              <Box sx={{ display: "flex" }}>
                <Grid container spacing={2}>
                  {selectButtons?.map((selectButton: SelectButton) => (
                    <Grid item sm={4} xs={12} key={selectButton.key}>
                      <IconButton
                        text={selectButton.text}
                        icon={selectButton.icon}
                        selected={values.checked == selectButton.key}
                        onClick={() => {
                          if (values.checked == selectButton.key) {
                            setFieldValue("checked", "");
                            setFieldValue("response", "");
                          } else {
                            setFieldValue("checked", selectButton.key);
                            if (selectButton.response) {
                              setFieldValue("response", selectButton.response);
                            }
                            setStatus(null);
                          }
                        }}
                      />
                    </Grid>
                  ))}
                  {values.response != "" && (
                    <Grid item xs={12}>
                      <Typography
                        sx={{
                          my: 2,
                        }}
                      >
                        {values.response}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <ScreenFlowFormButtons
                isSubmitting={isSubmitting}
                status={status}
                onBack={onBack}
              />
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};
